﻿import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Grid2 from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { createStyles, makeStyles } from "@mui/styles";
import AddBoxIcon from "@mui/icons-material/AddBox";
import SearchIcon from "@mui/icons-material/Search";
import type {
	ColDef,
	DefaultMenuItem,
	EditableCallbackParams,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
	RowNode,
	SelectionChangedEvent,
} from "ag-grid-community";
import "ag-grid-enterprise";
import { AgGridTooltip } from "@/components/ag-grid-tooltip";
import {
	getAssemblyTooltip,
	rowIsInAssembly,
} from "../AssembliesPanel/assemblyAnnotation";
import { AgGridReact } from "ag-grid-react";
import { debounce } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CustomLoadingOverlay from "../../../../../components/loaders/LazyLoading/LazyLoading";
import { useAssetDetailsContext } from "../../../context/assetDetailsContext";
import { addLinkAssetDocument } from "../../../repositories/AssetDetailsRepositorie";
import { useInitialValues } from "../../hooks/useInitialValues";
import { useSearchParamsToReducer } from "../../hooks/useSearchParamsToReducer";
import { DocumentsDownloadButtons } from "./components/download-buttons/documents-download-buttons.columns";
import { DocumentNameColumn } from "./components/editing-row/document-name.column";
import { DocumentAssetNameColumn } from "./components/editing-row/document-AssetName.column";
import { DocumentVersionColumn } from "./components/editing-row/document-Version.column";
import { DocumentDateTimeColumn } from "./components/editing-row/document-dateTime.column";
import { DocumentTitleColumn } from "./components/editing-row/document-title.column";
import { useAssetDocumentsGridActionsAndButtons } from "./hooks/useAssetDocumentsGridActionsAndButtons";
import { useSearchAssetDocuments } from "./hooks/useSearchAssetDocuments";
import { useSearchDocuments } from "./hooks/useSearchDocuments";
import { ErrorModal } from "@dexteel/mesf-core";
import FormatListBulletedSharpIcon from "@mui/icons-material/FormatListBulletedSharp";
import { globalDefaultColDef, mesfColors } from "@setup/ag-grid/mesf-theme";
import { AssetDocument } from "@/pages/asset-details/models/AssetDocument";
import { DocumentsCommentsColumn } from "./components/comments-buttons/comments-buttons.columns";
import { PropertiesModal } from "./components/properties-modal/properties-modal";
import { DocumentsPicker } from "./components/document-picker/document-picker";

const useStyles = makeStyles((theme) =>
	createStyles({
		agGridContainer: {
			position: "relative",
			display: "flex",
			flexDirection: "column",
			height: "100%",
		},
		searchToolbar: {
			display: "flex",
			alignItems: "center",
			gap: theme.spacing(1),
			padding: "8px 0px",
		},
		searchField: {
			flex: 1,
			marginRight: 0,
			"& .MuiOutlinedInput-root": {
				height: "40px",
			},
		},
		addButton: {
			minWidth: "40px",
			height: "40px",
			padding: 0,
		},
		gridWrapper: {
			flex: 1,
			minHeight: 0,
		},
	}),
);

type Props = {
	searchStatistics: Function;
};

const VITE_DOCUMENT_PATH =
	import.meta.env.VITE_DOCUMENT_PATH ||
	"https://mill-cmms.roanokesteel.com/vaultix/";

export const AssetDocumentsGrid = ({ searchStatistics }: Props) => {
	const navigate = useNavigate();
	const classes = useStyles();
	const [openPropertiesDialog, setOpenPropertiesDialog] =
		useState<boolean>(false);
	const [openDocumentsPicker, setOpenDocumentsPicker] =
		useState<boolean>(false);
	const [searchParams] = useSearchParams();
	const resource = searchParams.get("resource") ?? "";
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState("");
	const [errorApi, setErrorApi] = useState("");
	const [isAssetDocumentsLoading, setIsAssetDocumentsLoading] =
		useState<boolean>(false);
	const [assetDocumentsError, setAssetDocumentsError] = useState<string>("");
	const [linkDocumentError, setLinkDocumentError] = useState<string>("");
	const [removeDocumentError, setRemoveDocumentError] = useState<string>("");
	const [quickFilterText, setQuickFilterText] = useState("");
	const [includeDescendants, setIncludeDescendants] = useState(true);

	const {
		state: { assetSearchId, listAssetDocuments, documentSearch, rowSelected },
		actions: {
			setFirstTime,
			setShowListOfDocuments,
			setDocumentSearch,
			setRowSelected,
			setLoaderListSearchDocuments,
		},
	} = useAssetDetailsContext();

	const debouncedSearch = useRef(
		debounce((documentSearch?: string) => {
			searchDocuments(documentSearch || undefined).then(() => {
				setLoaderListSearchDocuments(false);
			});
		}, 3000),
	).current;

	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const containerStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
	const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
	const [listDocumentSelected, setListDocumentSelected] = useState<any[]>([]);
	const [columnDefs, setColumnDefs] = useState<ColDef[]>();

	const searchAssetDocuments = useSearchAssetDocuments({
		setIsAssetDocumentsLoading,
		setAssetDocumentsError,
	});

	const searchDocuments = useSearchDocuments({ setIsLoading, setError });

	const loadingOverlayComponent = useMemo<any>(() => {
		return CustomLoadingOverlay;
	}, []);

	const onActionsClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		params: EditableCallbackParams,
	) => {
		(params.api as any).showContextMenu({
			column: params.column,
			rowNode: params.node,
			undefined,
			mouseEvent: e.nativeEvent,
		});
	};

	const {
		refresh,
		removeSelected,
		saveButton,
		deleteOpen,
		setSaveButton,
		setDeleteOpen,
	} = useAssetDocumentsGridActionsAndButtons({
		gridRef: gridApi,
		searchStatistics,
		searchAssetDocuments,
		setRemoveDocumentError,
		includeDescendants,
	});

	const openDocumentPicker = useCallback(() => {
		searchDocuments();
		setOpenDocumentsPicker(true);
	}, [searchDocuments]);

	const handleDocumentPickerSave = useCallback(
		(shouldUpdate: boolean) => {
			setOpenDocumentsPicker(false);
			if (shouldUpdate) {
				refresh();
			}
		},
		[refresh],
	);

	const handleQuickFilterChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const value = event.target.value;
			setQuickFilterText(value);
			if (gridApi) {
				gridApi.setGridOption("quickFilterText", value);
			}
		},
		[gridApi],
	);

	const renderColumns = () => {
		setColumnDefs([]);
		const newColumns: ColDef[] = [];
		newColumns.push({
			field: "",
			flex: 1,
			minWidth: 30,
			maxWidth: 30,
			filter: false,
			checkboxSelection: true,
			headerCheckboxSelection: true,
			headerCheckboxSelectionFilteredOnly: true,
			getQuickFilterText: () => "",
		});
		newColumns.push({
			cellRenderer: DocumentNameColumn,
			type: "columnProperties",
			field: "DocumentName",
			headerName: "Document",
			flex: 2,
			minWidth: 150,
			maxWidth: 150,
			getQuickFilterText: (params) => params.data?.DocumentName || "",
		});
		newColumns.push({
			cellRenderer: DocumentTitleColumn,
			type: "columnProperties",
			field: "Title",
			headerName: "Title",
			flex: 4,
			minWidth: 290,
			getQuickFilterText: (params) => params.data?.Title || "",
		});
		newColumns.push({
			cellRenderer: DocumentAssetNameColumn,
			type: "columnProperties",
			field: "AssetName",
			headerName: "Asset",
			flex: 1,
			minWidth: 220,
			maxWidth: 220,
			getQuickFilterText: (params) => {
				const assetName = params.data?.AssetName || params.data?.FullPath || "";
				return assetName;
			},
		});
		newColumns.push({
			cellRenderer: DocumentDateTimeColumn,
			type: "columnProperties",
			valueFormatter: (params) => params.data.CreationDateTime?.slice(0, 10),
			field: "CreationDatetime",
			headerName: "Datetime",
			flex: 1,
			minWidth: 80,
			maxWidth: 80,
			getQuickFilterText: (params) => {
				const dateTime =
					params.data?.CreationDateTime || params.data?.CreationTimestamp;
				if (dateTime) {
					const fullDate = dateTime.toString();
					const shortDate = dateTime.slice(0, 10);
					return `${fullDate} ${shortDate}`;
				}
				return "";
			},
		});
		newColumns.push({
			cellRenderer: DocumentVersionColumn,
			type: "columnProperties",
			field: "Version",
			headerName: "Version",
			flex: 1,
			minWidth: 50,
			maxWidth: 50,
			getQuickFilterText: (params) => params.data?.Version?.toString() || "",
		});
		newColumns.push({
			cellRenderer: (params: any) =>
				DocumentsCommentsColumn({
					data: params?.data,
					searchAssetDocuments,
				}),
			field: "Comments",
			flex: 1,
			type: "dataName",
			minWidth: 90,
			getQuickFilterText: (params) => params.data?.Comments || "",
		});
		newColumns.push({
			cellRenderer: DocumentsDownloadButtons,
			field: "Download",
			flex: 1,
			minWidth: 200,
			editable: false,
			autoHeight: true,
			suppressNavigable: true,
			getQuickFilterText: () => "",
		});
		newColumns.push({
			headerName: "",
			flex: 1,
			minWidth: 65,
			maxWidth: 65,
			cellClass: "ag-columns",
			wrapText: false,
			autoHeight: false,
			getQuickFilterText: () => "",
			cellRenderer: (params: EditableCallbackParams) => {
				return (
					<Grid2
						container
						style={{
							height: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Grid2
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
							size={{ xs: 12, md: 12 }}
						>
							<Grid2
								container
								spacing={1}
								style={{ justifyContent: "flex-end", minWidth: 30 }}
							>
								<Grid2 size={{ xs: 12, md: 12 }}>
									<Button
										fullWidth
										style={{}}
										onClick={(e) => onActionsClick(e, params)}
									>
										<FormatListBulletedSharpIcon
											style={{ height: "auto" }}
											color="action"
										/>
									</Button>
								</Grid2>
							</Grid2>
						</Grid2>
					</Grid2>
				);
			},
		});
		setColumnDefs(newColumns);
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			if (gridApi && params.node) {
				const selectedNodes = gridApi.getSelectedNodes();
				const isCurrentlySelected = params.node.isSelected();

				if (selectedNodes.length > 1 && isCurrentlySelected) {
				} else if (!isCurrentlySelected) {
					gridApi.deselectAll();
					params.node.setSelected(true);
				}
			}

			setRowSelected(params.node?.data);
			const rowSelectedInGrid = params.node?.data;
			const selectedDocuments = gridApi?.getSelectedNodes();
			const drawginsLinksToPaste = JSON.parse(
				localStorage.getItem("documentCopied") as string,
			);
			const DocumentsCopiedInLocalStorage = () => {
				if (selectedDocuments?.length) {
					const selectedDocumentsId = selectedDocuments.map(
						(rowNode) => rowNode?.data,
					);
					localStorage.setItem(
						"documentCopied",
						JSON.stringify(selectedDocumentsId),
					);
				} else if (rowSelectedInGrid) {
					localStorage.setItem(
						"documentCopied",
						JSON.stringify(rowSelectedInGrid?.DocumentName),
					);
				}
			};

			const PasteDocumentsCopiedInLocalStorage = async () => {
				try {
					let documentNames: string[] = [];

					// Collect all document names
					if (Array.isArray(drawginsLinksToPaste)) {
						documentNames = drawginsLinksToPaste
							.map((item) => item.DocumentName)
							.filter((name) => name);
					} else if (typeof drawginsLinksToPaste === "string") {
						documentNames = [drawginsLinksToPaste];
					}

					if (documentNames.length === 0) {
						setError("No documents to paste");
						return;
					}

					// Single bulk API call
					const bulkResult = await addLinkAssetDocument(
						assetSearchId as string,
						documentNames, // Pass array directly, function will handle it
						"",
					);

					if (!bulkResult.ok) {
						setLinkDocumentError(bulkResult.message);
						return;
					}

					// Parse results
					const result = bulkResult.data?.tables?.[0]?.rows?.[0];
					if (result) {
						const newlyLinkedCount = result.NewlyLinkedCount || 0;
						const alreadyLinkedCount = result.AlreadyLinkedCount || 0;
						const alreadyLinkedDocuments = result.AlreadyLinkedDocuments;

						// Show summary message
						let message = `Successfully processed ${documentNames.length} documents:\n`;
						message += `• ${newlyLinkedCount} newly linked\n`;
						if (alreadyLinkedCount > 0) {
							message += `• ${alreadyLinkedCount} already linked: ${alreadyLinkedDocuments}`;
						}

						if (alreadyLinkedCount > 0) {
							setError(message); // Show as warning for user awareness
						}
					}

					refresh();
				} catch (error) {
					console.error("Error pasting documents:", error);
					setError("Error pasting documents");
				}
			};

			const DownloadOriginal = () => {
				const link = document.createElement("a");
				link.href = `${VITE_DOCUMENT_PATH}${params.node?.data.DocPath}`;
				link.setAttribute("target", `_blank}`);
				link.setAttribute(
					"download",
					`${params.node?.data.Title} ${params.node?.data.DocPath}`,
				);
				document.body.appendChild(link);
				link.click();
			};

			const DownloadPDF = () => {
				const link = document.createElement("a");
				link.href = `${VITE_DOCUMENT_PATH}${params.node?.data.PDFPath}`;
				link.setAttribute("target", `_blank}`);
				link.setAttribute(
					"download",
					`${params.node?.data.Title} ${params.node?.data?.PDFPath}`,
				);
				document.body.appendChild(link);
				link.click();
			};

			const result: (DefaultMenuItem | MenuItemDef)[] = [];

			if (
				rowSelectedInGrid &&
				rowSelectedInGrid?.id !== -1 &&
				(selectedDocuments?.length ?? 0) <= 1
			) {
				result.push({
					name: "Properties",
					action: () => {
						setOpenPropertiesDialog(true);
						setRowSelected(params.node?.data);
					},
					icon: '<i class="fas fa-file-invoice"></i>',
				});
			}

			if (
				rowSelectedInGrid &&
				rowSelectedInGrid?.id !== -1 &&
				(selectedDocuments?.length ?? 0) <= 1
			) {
				result.push({
					name: "Go to Asset",
					action: () => {
						setFirstTime(true);
						setSaveButton(false);
						setDeleteOpen(false);
						setDocumentSearch("");
						setTimeout(() => {
							navigate(`/asset-details/${params.node?.data.AssetId}`, {
								replace: true,
							});
							setIsAssetDocumentsLoading(false);
						}, 100);
					},
					icon: '<i class="fas fa-external-link-alt"></i>',
				});
			}

			if (selectedDocuments?.length || rowSelectedInGrid) {
				result.push({
					name:
						(selectedDocuments?.length ?? 0) > 1
							? "Copy Document Links"
							: "Copy Document Link",
					action: DocumentsCopiedInLocalStorage,
					icon: '<i class="far fa-copy"></i>',
				});
			}
			if (
				Array.isArray(drawginsLinksToPaste) &&
				drawginsLinksToPaste?.length &&
				(selectedDocuments?.length ?? 0) <= 1
			) {
				result.push({
					name: "Paste Document Link",
					action: PasteDocumentsCopiedInLocalStorage,
					icon: '<i class="fas fa-paste"></i>',
				});
			}
			if (
				!Array.isArray(drawginsLinksToPaste) &&
				typeof drawginsLinksToPaste === "string" &&
				(selectedDocuments?.length ?? 0) <= 1
			) {
				result.push({
					name: "Paste Document Link",
					action: PasteDocumentsCopiedInLocalStorage,
					icon: '<i class="fas fa-paste"></i>',
				});
			}

			if ((selectedDocuments?.length ?? 0) <= 1) {
				result.push({
					name: "Add Document Link",
					action: openDocumentPicker,
					icon: '<i class="fas fa-plus"></i>',
				});
			}

			if (selectedDocuments?.length) {
				// A document can only be unlinked from the asset it belongs to; disable Remove (with an
				// explaining tooltip) when any selected document is linked to another asset, so the
				// user never triggers the cross-asset unlink error.
				const allOwned = selectedDocuments.every(
					(n) => Number(n.data?.AssetId) === Number(assetSearchId),
				);
				result.push({
					name:
						(selectedDocuments?.length ?? 0) > 1
							? "Remove Document Links"
							: "Remove Document Link",
					disabled: !allOwned,
					tooltip: allOwned
						? undefined
						: 'Go to the document\'s asset ("Go to Asset") to remove it',
					action: () => {
						if (!allOwned) return;
						setDeleteOpen(true);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}

			if (rowSelectedInGrid && (selectedDocuments?.length ?? 0) <= 1) {
				if (params.node?.data.PDFPath) {
					result.push({
						name: "Download PDF",
						action: DownloadPDF,
						icon: '<i class="far fa-file-pdf"></i>',
					});
				}
				if (params.node?.data.DocPath) {
					result.push({
						name: "Download original",
						action: DownloadOriginal,
						icon: '<i class="far fa-file-alt"></i>',
					});
				}
			}

			if (
				!selectedDocuments?.length &&
				rowSelectedInGrid?.id !== -1 &&
				rowSelectedInGrid
			) {
				const owned =
					Number(rowSelectedInGrid.AssetId) === Number(assetSearchId);
				result.push({
					name: "Remove Document Link",
					disabled: !owned,
					tooltip: owned
						? undefined
						: 'Go to the document\'s asset ("Go to Asset") to remove it',
					action: () => {
						if (!owned) return;
						setRowSelected(params.node?.data);
						setListDocumentSelected([]);
						setDeleteOpen(true);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}

			return result;
		},
		[
			listAssetDocuments,
			saveButton,
			openDocumentPicker,
			assetSearchId,
			gridApi,
			rowSelected,
		],
	);

	const onSelectionChanged = useCallback((event: SelectionChangedEvent) => {
		const rowCount = event.api.getSelectedNodes().length;
		const listDocumentsSelected = event.api.getSelectedNodes();
		setListDocumentSelected(listDocumentsSelected);
	}, []);

	const openModalViewerActive = () => {
		const selectedRow: RowNode<any>[] | any = gridApi?.getSelectedNodes();
		if (selectedRow[0]!.data.AssetName !== undefined) {
			setRowSelected(selectedRow[0]!.data);
			setOpenPropertiesDialog(!openPropertiesDialog);
		}
	};

	useEffect(() => {
		if (documentSearch.length >= 5) {
			setLoaderListSearchDocuments(true);
			setShowListOfDocuments(true);
			debouncedSearch(documentSearch);
		} else {
			setShowListOfDocuments(false);
		}
	}, [documentSearch]);

	useEffect(() => {
		return () => {
			debouncedSearch.cancel();
		};
	}, [debouncedSearch]);

	useInitialValues();
	useSearchParamsToReducer();

	useEffect(() => {
		setSaveButton(false);
		setDocumentSearch("");
	}, [assetSearchId]);

	useEffect(() => {
		renderColumns();
	}, []);

	useEffect(() => {
		// Only search if we have a valid assetSearchId (not 0, undefined, or null)
		// This prevents the initial call with assetSearchId=0 when the component first mounts
		if (resource === "documents" && assetSearchId && assetSearchId !== 0) {
			searchAssetDocuments(includeDescendants);
		}
	}, [resource, assetSearchId, includeDescendants]);

	return (
		<>
			<div style={containerStyle}>
				<Grid2 container style={gridStyle}>
					<Grid2 className={classes.agGridContainer} size={{ md: 12, xs: 12 }}>
						<div className={classes.searchToolbar}>
							<TextField
								className={classes.searchField}
								variant="outlined"
								placeholder="Search documents..."
								value={quickFilterText}
								onChange={handleQuickFilterChange}
								slotProps={{
									input: {
										startAdornment: (
											<SearchIcon color="action" style={{ marginRight: 8 }} />
										),
									},
								}}
								size="small"
							/>
							<FormControlLabel
								control={
									<Switch
										checked={includeDescendants}
										onChange={(e) => setIncludeDescendants(e.target.checked)}
										color="primary"
									/>
								}
								label="Include descendant assets"
								style={{ marginLeft: 16, marginRight: 16 }}
							/>
							<IconButton
								onClick={openDocumentPicker}
								aria-label="add documents"
								color="primary"
								className={classes.addButton}
							>
								<AddBoxIcon style={{ fontSize: 40, fontWeight: "bold" }} />
							</IconButton>
						</div>

						<div className={classes.gridWrapper}>
							<AgGridReact
								onGridReady={(params) => setGridApi(params.api)}
								rowData={listAssetDocuments}
								columnDefs={columnDefs}
								// Assembly tooltip on every cell so hovering anywhere on a tinted row shows
								// which assemblies contain the drawing (returns undefined otherwise).
								defaultColDef={{
									...globalDefaultColDef,
									tooltipComponent: "agGridTooltip",
									tooltipValueGetter: (p) => getAssemblyTooltip(p.data),
								}}
								components={{ agGridTooltip: AgGridTooltip }}
								tooltipShowDelay={300}
								getRowStyle={(params) =>
									rowIsInAssembly(params.data)
										? { background: mesfColors.resource.drawings }
										: undefined
								}
								headerHeight={32}
								rowSelection={"multiple"}
								animateRows={true}
								getContextMenuItems={getContextMenuItems}
								onSelectionChanged={onSelectionChanged}
								loadingOverlayComponent={loadingOverlayComponent}
								onRowDoubleClicked={openModalViewerActive}
								loading={isAssetDocumentsLoading}
								quickFilterText={quickFilterText}
							/>
						</div>

						<DocumentsPicker
							searchAssetDocuments={searchAssetDocuments}
							show={openDocumentsPicker}
							isLoading={isLoading}
							onHide={handleDocumentPickerSave}
							AssetId={assetSearchId as number}
						/>
					</Grid2>
					<Dialog open={deleteOpen} aria-labelledby="responsive-dialog-title">
						<DialogTitle id="responsive-dialog-title">{"WARNING"}</DialogTitle>
						<DialogContent>
							<DialogContentText style={{ padding: 0, margin: 0 }}>
								{listDocumentSelected?.length === 1 ? (
									<>
										<p style={{ margin: "0" }}>
											UNLINK <b>{listDocumentSelected[0].data.Title}</b> FROM
											ASSET <b>{listDocumentSelected[0].data.AssetName}?</b>
										</p>
									</>
								) : listDocumentSelected.length > 1 ? (
									<>
										<p style={{ margin: 0 }}> UNLINK:</p>
										{listDocumentSelected?.map((document) => {
											return (
												<>
													<p style={{ margin: "5px 0" }}>
														<b>• {document.data.Title}, </b>
													</p>
													<p style={{ margin: 0 }}>
														FROM ASSET <b>{document.data.AssetName}.</b>
													</p>
												</>
											);
										})}
									</>
								) : rowSelected?.DocumentName &&
									listDocumentSelected?.length === 0 ? (
									<>
										<p style={{ margin: "0" }}>
											UNLINK <b>{rowSelected?.Title}</b> FROM ASSET{" "}
											<b>{rowSelected?.AssetName}?</b>
										</p>
									</>
								) : (
									<>
										<h1>Something went wrong</h1>
									</>
								)}
							</DialogContentText>
						</DialogContent>
						<DialogActions style={{ padding: "16px 24px" }}>
							<Button
								onClick={() => setDeleteOpen(false)}
								autoFocus
								color="inherit"
							>
								CANCEL
							</Button>
							<Button autoFocus color="secondary" onClick={removeSelected}>
								UNLINK
							</Button>
						</DialogActions>
					</Dialog>
				</Grid2>
				<PropertiesModal
					searchAssetDocuments={searchAssetDocuments}
					show={openPropertiesDialog}
					rowSelected={rowSelected as any}
					onHide={(shouldUpdate) => {
						if (shouldUpdate) {
							setOpenPropertiesDialog(false);
							searchAssetDocuments(includeDescendants);
						} else {
							setOpenPropertiesDialog(false);
						}
					}}
				/>
				<ErrorModal error={error} onHide={() => setError("")} />
				<ErrorModal error={errorApi} onHide={() => setErrorApi("")} />
				<ErrorModal
					error={assetDocumentsError}
					onHide={() => setAssetDocumentsError("")}
				/>
				<ErrorModal
					error={linkDocumentError}
					onHide={() => setLinkDocumentError("")}
				/>
				<ErrorModal
					error={removeDocumentError}
					onHide={() => setRemoveDocumentError("")}
				/>
			</div>
		</>
	);
};
